/**
 * API health service — powers the live/demo status indicator.
 *
 * Pings the backend readiness probe (<API_BASE_URL>/health) with a short
 * timeout so the UI can tell the user, honestly, whether it is showing real
 * data from the Node/MariaDB backend or falling back to local mock data.
 *
 * This intentionally does NOT use the shared http client: the health check
 * must be fast-failing (4s) and must never throw, so the badge can always
 * render something meaningful.
 */

import { API_BASE_URL, isApiConfigured } from "./http";

export interface ApiHealth {
  /** Whether VITE_PUBLIC_API_URL is set at all. */
  configured: boolean;
  /** True only when the API responded 2xx AND the database reported "up". */
  live: boolean;
  status?: string;
  database?: string;
  version?: string;
  detail?: string;
  checkedAt: number;
}

const HEALTH_TIMEOUT_MS = 4_000;

interface HealthEnvelope {
  success?: boolean;
  data?: {
    status?: string;
    database?: string;
    version?: string;
    detail?: string;
  };
}

/** Best-effort ping of the backend readiness probe. Never throws. */
export async function checkApiHealth(): Promise<ApiHealth> {
  const checkedAt = Date.now();

  if (!isApiConfigured()) {
    return { configured: false, live: false, checkedAt };
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), HEALTH_TIMEOUT_MS);

  try {
    const response = await fetch(`${API_BASE_URL}/health`, {
      method: "GET",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });

    const envelope = (await response.json()) as HealthEnvelope;
    const data = envelope.data ?? {};

    return {
      configured: true,
      live: response.ok && data.database === "up",
      status: data.status,
      database: data.database,
      version: data.version,
      detail: data.detail,
      checkedAt,
    };
  } catch {
    // Network error, timeout, or non-JSON body — treat as "unreachable".
    return { configured: true, live: false, checkedAt };
  } finally {
    clearTimeout(timer);
  }
}